/**
 * Add Ship Page
 * ==============
 * صفحة إضافة شيب جديد مع نظام المتطلبات الديناميكي
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../shared/PageStyles.css';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to handle map clicks
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
};

// Component to update map center when it changes
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

const I = {
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  upload: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  map: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  link: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  loading: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeLinecap="round"/></svg>,
};

const groupIcons = {
  'info-circle': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  'map-marker-alt': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  'phone': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  'clock': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  'user': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  'file-alt': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  'upload': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  'image': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
};

const regionOptions = {
  riyadh: 'الرياض',
  makkah: 'مكة المكرمة',
  madinah: 'المدينة المنورة',
  eastern: 'المنطقة الشرقية',
  asir: 'عسير',
  tabuk: 'تبوك',
  hail: 'حائل',
  northern: 'الحدود الشمالية',
  jazan: 'جازان',
  najran: 'نجران',
  bahah: 'الباحة',
  jawf: 'الجوف',
  qassim: 'القصيم',
};

// Groups to exclude (owner info not needed - company owns the ships)
const EXCLUDED_GROUPS = ['owner_info'];

const AddShipPage = () => {
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [activeGroup, setActiveGroup] = useState(0);
  const [extractingCoords, setExtractingCoords] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 24.7136, lng: 46.6753 }); // Default: Riyadh
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allWaterTypes, setAllWaterTypes] = useState([]);
  const [selectedWaterTypes, setSelectedWaterTypes] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catsRes, reqsRes, wtRes] = await Promise.all([
        apiCall('/tenant/ship-requirements/categories'),
        apiCall('/tenant/ship-requirements/requirements'),
        apiCall('/ships/water-types/all'),
      ]);
      
      setCategories(catsRes || []);
      setAllWaterTypes(wtRes?.items || []);
      
      // Filter out excluded groups (like owner_info)
      const filteredReqs = (reqsRes.requirements || []).filter(r => !EXCLUDED_GROUPS.includes(r.group_key));
      const filteredGroups = (reqsRes.groups || []).filter(g => !EXCLUDED_GROUPS.includes(g.key));
      
      setRequirements(filteredReqs);
      setGroups(filteredGroups);
      
      // Initialize form data with defaults
      const initialData = {};
      (reqsRes.requirements || []).forEach(req => {
        initialData[req.field_key] = req.default_value || '';
      });
      setFormData(initialData);
      
    } catch (err) {
      showError('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (fieldKey, value) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
    // Clear error when user starts typing
    if (errors[fieldKey]) {
      setErrors(prev => ({ ...prev, [fieldKey]: null }));
    }
  };

  // دالة للحصول على العنوان من الإحداثيات
  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await apiCall('/tenant/ship-requirements/reverse-geocode', {
        method: 'POST',
        body: JSON.stringify({ latitude: lat, longitude: lng })
      });

      if (res.success) {
        // ملء حقول العنوان تلقائياً
        setFormData(prev => ({
          ...prev,
          city: res.city || prev.city || '',
          district: res.district || prev.district || '',
          street: res.street || prev.street || '',
          building_number: res.building_number || prev.building_number || '',
          postal_code: res.postal_code || prev.postal_code || '',
          region: res.region || prev.region || ''
        }));
        
        if (res.city || res.district) {
          success('تم التعرف على العنوان تلقائياً');
        }
      }
    } catch (err) {
      console.log('Reverse geocode failed:', err);
    }
  };

  const extractCoordinatesFromUrl = async () => {
    const url = formData.google_maps_link;
    if (!url) {
      showError('الرجاء إدخال رابط خرائط جوجل');
      return;
    }

    setExtractingCoords(true);
    try {
      const res = await apiCall('/tenant/ship-requirements/extract-coordinates', {
        method: 'POST',
        body: JSON.stringify({ url })
      });

      if (res.success) {
        // تحديث الإحداثيات والعنوان معاً (البيانات تأتي من الـ API مباشرة)
        setFormData(prev => ({
          ...prev,
          latitude: res.latitude?.toString() || '',
          longitude: res.longitude?.toString() || '',
          // بيانات العنوان المستخرجة تلقائياً
          region: res.region || prev.region || '',
          city: res.city || prev.city || '',
          district: res.district || prev.district || '',
          street: res.street || prev.street || '',
          building_number: res.building_number || prev.building_number || '',
          postal_code: res.postal_code || prev.postal_code || ''
        }));
        
        // رسالة نجاح مفصلة
        if (res.city || res.district || res.street) {
          success('تم استخراج الإحداثيات والعنوان بنجاح');
        } else {
          success('تم استخراج الإحداثيات بنجاح');
        }
      } else {
        showError(res.message || 'فشل استخراج الإحداثيات');
      }
    } catch (err) {
      showError('فشل استخراج الإحداثيات من الرابط');
    } finally {
      setExtractingCoords(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    requirements.forEach(req => {
      if (req.is_required) {
        const value = formData[req.field_key];
        if (!value || (typeof value === 'string' && !value.trim())) {
          newErrors[req.field_key] = `${req.field_name_ar} مطلوب`;
        }
      }
      
      // Validate regex if provided
      if (req.validation_regex && formData[req.field_key]) {
        try {
          const regex = new RegExp(req.validation_regex);
          if (!regex.test(formData[req.field_key])) {
            newErrors[req.field_key] = req.validation_message_ar || req.validation_message || 'القيمة غير صحيحة';
          }
        } catch (e) {
          // Invalid regex, skip validation
        }
      }
      
      // Validate min/max for numbers
      if (req.field_type === 'number' && formData[req.field_key]) {
        const num = parseFloat(formData[req.field_key]);
        if (req.min_value !== null && num < req.min_value) {
          newErrors[req.field_key] = `القيمة يجب أن تكون ${req.min_value} على الأقل`;
        }
        if (req.max_value !== null && num > req.max_value) {
          newErrors[req.field_key] = `القيمة يجب أن تكون ${req.max_value} كحد أقصى`;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('الرجاء تعبئة جميع الحقول المطلوبة بشكل صحيح');
      // Find first group with error
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        const firstErrorField = requirements.find(r => errorKeys.includes(r.field_key));
        if (firstErrorField) {
          const groupIndex = groups.findIndex(g => g.key === firstErrorField.group_key);
          if (groupIndex !== -1) setActiveGroup(groupIndex);
        }
      }
      return;
    }

    setSubmitting(true);
    try {
      // Collect files to upload
      const filesToUpload = [];
      
      // Filter out file objects and internal fields, keep only string values
      const reqData = Object.entries(formData)
        .filter(([key, value]) => {
          // Collect files for later upload
          if (key.endsWith('_file') && value instanceof File) {
            filesToUpload.push({ fieldKey: key.replace('_file', ''), file: value });
            return false;
          }
          // Skip internal fields
          if (key.endsWith('_uploading') || key.endsWith('_file') || key.endsWith('_url')) {
            return false;
          }
          // Skip File objects
          if (value instanceof File) {
            filesToUpload.push({ fieldKey: key, file: value });
            return false;
          }
          return true;
        })
        .map(([field_key, value]) => ({
          field_key,
          value: typeof value === 'object' ? JSON.stringify(value) : String(value || '')
        }));

      // Create ship first
      const res = await apiCall('/tenant/ship-requirements/ships', {
        method: 'POST',
        body: JSON.stringify({
          category_id: selectedCategory || null,
          requirements: reqData
        })
      });

      const shipId = res.id;

      // Save water types after ship is created
      if (selectedWaterTypes.length > 0) {
        try {
          await apiCall(`/ships/${shipId}/water-types`, {
            method: 'PUT',
            body: JSON.stringify({ codes: selectedWaterTypes }),
          });
        } catch (wtErr) {
          console.error('Failed to save water types:', wtErr);
        }
      }

      // Upload files after ship is created
      for (const { fieldKey, file } of filesToUpload) {
        try {
          const uploadForm = new FormData();
          uploadForm.append('file', file);
          uploadForm.append('ship_id', shipId);
          uploadForm.append('document_type', fieldKey);

          const token = localStorage.getItem('watrly_tenant_token');
          await fetch('http://localhost:8002/api/v1/tenant/uploads/ship-document', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: uploadForm
          });
        } catch (uploadErr) {
          console.error('File upload failed:', fieldKey, uploadErr);
        }
      }

      success(res.message || 'تم إنشاء الشيب بنجاح');
      navigate('/tenant/ships');
    } catch (err) {
      showError(err.message || 'فشل إنشاء الشيب');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.field_key] || '';
    const error = errors[field.field_key];
    const commonProps = {
      id: field.field_key,
      value,
      onChange: (e) => handleChange(field.field_key, e.target.value),
      placeholder: field.placeholder_ar || field.placeholder || '',
      className: error ? 'input-error' : '',
      disabled: submitting
    };

    switch (field.field_type) {
      case 'textarea':
        return <textarea {...commonProps} rows={3} />;
      
      case 'select':
        const options = field.options || [];
        return (
          <select {...commonProps}>
            <option value="">-- اختر --</option>
            {field.field_key === 'region' ? (
              Object.entries(regionOptions).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))
            ) : (
              options.map((opt, i) => (
                <option key={i} value={typeof opt === 'object' ? opt.value : opt}>
                  {typeof opt === 'object' ? (opt.label_ar || opt.label_en) : opt}
                </option>
              ))
            )}
          </select>
        );
      
      case 'multi_select':
        const multiOptions = field.options || [];
        const selectedValues = Array.isArray(value) ? value : (value ? JSON.parse(value) : []);
        return (
          <div className="multi-select-group">
            {multiOptions.map((opt, i) => {
              const optValue = typeof opt === 'object' ? opt.value : opt;
              const optLabel = typeof opt === 'object' ? (opt.label_ar || opt.label_en) : opt;
              const isChecked = selectedValues.includes(optValue);
              return (
                <label key={i} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...selectedValues, optValue]
                        : selectedValues.filter(v => v !== optValue);
                      handleChange(field.field_key, JSON.stringify(newValues));
                    }}
                    disabled={submitting}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
        );
      
      case 'date':
        return <input type="date" {...commonProps} />;
      
      case 'time':
        return <input type="time" {...commonProps} />;
      
      case 'number':
        return (
          <input
            type="number"
            {...commonProps}
            min={field.min_value}
            max={field.max_value}
            step="any"
          />
        );
      
      case 'phone':
        return (
          <input
            type="tel"
            {...commonProps}
            dir="ltr"
            style={{ textAlign: 'left' }}
          />
        );
      
      case 'email':
        return <input type="email" {...commonProps} dir="ltr" style={{ textAlign: 'left' }} />;
      
      case 'url':
        if (field.field_key === 'google_maps_link') {
          return (
            <div className="url-with-action-group">
              <div className="url-with-action">
                <input type="url" {...commonProps} dir="ltr" style={{ textAlign: 'left' }} placeholder="الصق رابط خرائط جوجل هنا..." />
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={extractCoordinatesFromUrl}
                  disabled={extractingCoords || submitting}
                >
                  {extractingCoords ? I.loading : I.link}
                  <span>استخراج</span>
                </button>
              </div>
              <button
                type="button"
                className="btn btn--outline-primary map-picker-btn"
                onClick={() => {
                  // محاولة الكشف عن الموقع الحالي
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        setMapCenter({
                          lat: position.coords.latitude,
                          lng: position.coords.longitude
                        });
                        setShowMapPicker(true);
                      },
                      () => {
                        // إذا فشل، افتح الخريطة بالموقع الافتراضي (الرياض)
                        setShowMapPicker(true);
                      },
                      { enableHighAccuracy: true, timeout: 5000 }
                    );
                  } else {
                    setShowMapPicker(true);
                  }
                }}
              >
                {I.map}
                <span>اختر من الخريطة</span>
              </button>
            </div>
          );
        }
        return <input type="url" {...commonProps} dir="ltr" style={{ textAlign: 'left' }} />;
      
      case 'file':
        const isUploading = formData[`${field.field_key}_uploading`];
        const uploadedUrl = formData[`${field.field_key}_url`];
        return (
          <div className="file-upload">
            {uploadedUrl && (
              <div className="uploaded-file">
                {uploadedUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <img src={`http://localhost:8002${uploadedUrl}`} alt="معاينة" className="file-preview" />
                ) : (
                  <div className="file-icon">📄</div>
                )}
                <span className="file-uploaded-text">تم الرفع ✓</span>
              </div>
            )}
            <input
              type="file"
              id={field.field_key}
              accept={field.file_types ? field.file_types.split(',').map(t => `.${t}`).join(',') : 'image/*,.pdf'}
              multiple={field.max_files > 1}
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  const file = files[0];
                  handleChange(`${field.field_key}_uploading`, true);
                  handleChange(field.field_key, `جاري رفع ${file.name}...`);
                  
                  try {
                    const formDataUpload = new FormData();
                    formDataUpload.append('file', file);
                    formDataUpload.append('ship_id', 'temp');
                    formDataUpload.append('document_type', field.field_key);
                    
                    // Store file for later upload after ship is created
                    handleChange(`${field.field_key}_file`, file);
                    handleChange(field.field_key, file.name);
                    handleChange(`${field.field_key}_uploading`, false);
                  } catch (err) {
                    handleChange(field.field_key, '');
                    handleChange(`${field.field_key}_uploading`, false);
                  }
                }
              }}
              disabled={submitting || isUploading}
              style={{ display: 'none' }}
            />
            <label htmlFor={field.field_key} className={`file-upload-label ${isUploading ? 'uploading' : ''}`}>
              {isUploading ? I.loading : I.upload}
              <span>{isUploading ? 'جاري الرفع...' : (value || 'اختر ملف...')}</span>
            </label>
            {field.file_types && (
              <small className="file-hint">
                الأنواع المسموحة: {field.file_types} | الحد الأقصى: {field.max_file_size ? `${field.max_file_size / 1024} MB` : '10 MB'}
              </small>
            )}
          </div>
        );
      
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-loading"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page add-ship-page">
      <div className="page-header">
        <div className="page-header__title">
          <button className="btn btn--ghost" onClick={() => navigate('/tenant/ships')}>
            {I.back}
          </button>
          <div>
            <h1>إضافة شيب جديد</h1>
            <p>أدخل بيانات المحطة للمراجعة والموافقة</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Category Selection */}
        {categories.length > 0 && (
          <div className="category-section">
            <div className="section-title">فئة المحطة</div>
            <div className="category-grid">
              {categories.map(cat => (
                <label
                  key={cat.id}
                  className={`category-card ${selectedCategory === cat.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.id}
                    checked={selectedCategory === cat.id}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  />
                  <div className="category-card__content">
                    <strong>{cat.name_ar || cat.name}</strong>
                    {cat.description_ar && <small>{cat.description_ar}</small>}
                    {cat.min_daily_capacity_tons && (
                      <span className="capacity-hint">
                        السعة: {cat.min_daily_capacity_tons} - {cat.max_daily_capacity_tons || '∞'} طن
                      </span>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="progress-bar">
          {groups.map((_, index) => (
            <div 
              key={index} 
              className={`progress-step ${index < activeGroup ? 'completed' : ''} ${index === activeGroup ? 'active' : ''}`}
            />
          ))}
        </div>

        {/* Requirements Groups Tabs */}
        <div className="tabs-wrapper">
          <div className="tabs">
            {groups.map((group, index) => (
              <button
                key={group.key}
                type="button"
                className={`tab ${activeGroup === index ? 'active' : ''}`}
                onClick={() => setActiveGroup(index)}
              >
                {groupIcons[group.icon] || I.info}
                <span>{group.name_ar}</span>
                {group.fields.some(f => errors[f.field_key]) && (
                  <span className="error-dot" />
                )}
              </button>
            ))}
          </div>

          {/* Active Group Fields */}
          {groups[activeGroup] && (
            <div className="form-content">
              <div className="form-section-title">{groups[activeGroup].name_ar}</div>
              <div className="form-grid">
                {groups[activeGroup].fields
                  .filter(field => {
                    // إخفاء حقول الساعات عند اختيار 24 ساعة
                    if ((field.field_key === 'opening_time' || field.field_key === 'closing_time') && 
                        formData.working_hours_type === '24_hours') {
                      return false;
                    }
                    return true;
                  })
                  .map(field => (
                  <div
                    key={field.field_key}
                    className={`form-group ${field.field_type === 'textarea' || field.field_type === 'file' ? 'full-width' : ''}`}
                  >
                    <label htmlFor={field.field_key}>
                      {field.is_required && <span className="required">*</span>}
                      {field.field_name_ar}
                      {field.needs_verification && (
                        <span className="verification-badge" title="يحتاج مراجعة">
                          {I.check}
                        </span>
                      )}
                    </label>
                    {renderField(field)}
                    {errors[field.field_key] && (
                      <span className="error-text">{errors[field.field_key]}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Location Helper */}
              {groups[activeGroup].key === 'location_info' && (
                <div className="location-helper">
                  <div className="helper-card">
                    {I.map}
                    <div>
                      <strong>كيفية الحصول على الإحداثيات:</strong>
                      <ol>
                        <li>افتح خرائط جوجل على الموقع المطلوب</li>
                        <li>انسخ رابط الموقع من شريط العنوان</li>
                        <li>الصق الرابط في حقل "رابط الموقع"</li>
                        <li>اضغط زر "استخراج الإحداثيات"</li>
                      </ol>
                      <small>أو يمكنك إدخال الإحداثيات مباشرة</small>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Water Type Selector */}
        {allWaterTypes.length > 0 && (
          <div className="water-type-section">
            <div className="section-title">أنواع المياه المتوفرة في الشيب</div>
            <p className="section-hint">اختر أنواع المياه التي يمكن للشيب توفيرها للطلبات</p>
            <div className="water-type-grid">
              {allWaterTypes.map(wt => {
                const isSelected = selectedWaterTypes.includes(wt.code);
                return (
                  <label
                    key={wt.code}
                    className={`water-type-card ${isSelected ? 'selected' : ''}`}
                    style={{ '--wt-color': wt.color_code || '#3b82f6' }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        setSelectedWaterTypes(prev =>
                          e.target.checked
                            ? [...prev, wt.code]
                            : prev.filter(c => c !== wt.code)
                        );
                      }}
                      disabled={submitting}
                    />
                    <div className="water-type-card__body">
                      <strong>{wt.name_ar || wt.name_en}</strong>
                      {wt.description_ar && <small>{wt.description_ar}</small>}
                    </div>
                    {isSelected && (
                      <span className="water-type-check">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg>
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation & Submit */}
        <div className="form-actions">
          <div className="nav-buttons">
            {activeGroup > 0 && (
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setActiveGroup(prev => prev - 1)}
              >
                {I.back} السابق
              </button>
            )}
            {activeGroup < groups.length - 1 && (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setActiveGroup(prev => prev + 1)}
              >
                التالي
              </button>
            )}
          </div>
          
          <button
            type="submit"
            className="btn btn--success"
            disabled={submitting}
          >
            {submitting ? (
              <>{I.loading} <span>جاري الإرسال...</span></>
            ) : (
              <>{I.check} <span>إرسال للمراجعة</span></>
            )}
          </button>
        </div>
      </form>

      {/* Map Picker Modal */}
      {showMapPicker && (
        <div className="map-modal-overlay" onClick={() => setShowMapPicker(false)}>
          <div className="map-modal" onClick={e => e.stopPropagation()}>
            <div className="map-modal-header">
              <h3>اختر موقع المحطة</h3>
              <button className="close-btn" onClick={() => setShowMapPicker(false)}>✕</button>
            </div>
            <div className="map-modal-body">
              <div className="map-search-box">
                <div className="search-input-wrapper">
                  <input 
                    type="text" 
                    placeholder="ابحث عن موقع (مثل: ستاربكس الرياض)..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (searchQuery.trim()) {
                          setIsSearching(true);
                          try {
                            // استخدام Nominatim مع خيارات محسنة للبحث العربي
                            const res = await fetch(
                              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery + ' Saudi Arabia')}&limit=5&addressdetails=1&accept-language=ar`
                            );
                            const data = await res.json();
                            setSearchResults(data || []);
                            if (data && data[0]) {
                              setMapCenter({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                            }
                          } catch (err) {
                            console.error('Search error:', err);
                          }
                          setIsSearching(false);
                        }
                      }
                    }}
                  />
                  <button 
                    type="button" 
                    className="search-btn"
                    onClick={async () => {
                      if (searchQuery.trim()) {
                        setIsSearching(true);
                        try {
                          const res = await fetch(
                            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery + ' Saudi Arabia')}&limit=5&addressdetails=1&accept-language=ar`
                          );
                          const data = await res.json();
                          setSearchResults(data || []);
                          if (data && data[0]) {
                            setMapCenter({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                          }
                        } catch (err) {
                          console.error('Search error:', err);
                        }
                        setIsSearching(false);
                      }
                    }}
                    disabled={isSearching}
                  >
                    {isSearching ? I.loading : 'بحث'}
                  </button>
                </div>
                {/* نتائج البحث */}
                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map((result, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="search-result-item"
                        onClick={() => {
                          const lat = parseFloat(result.lat);
                          const lng = parseFloat(result.lon);
                          setMapCenter({ lat, lng });
                          
                          // استخراج بيانات العنوان من addressdetails
                          const addr = result.address || {};
                          const displayName = result.display_name || '';
                          console.log('Nominatim address data:', addr);
                          console.log('Display name:', displayName);
                          
                          // استخراج المدينة من display_name - البحث عن "محافظة X"
                          let city = '';
                          const mohafazaMatch = displayName.match(/محافظة\s+([^،,]+)/);
                          if (mohafazaMatch) {
                            city = mohafazaMatch[1].trim();
                          } else if (addr.county) {
                            city = addr.county.replace('محافظة ', '').replace('بلدية ', '').trim();
                          } else if (addr.state_district) {
                            city = addr.state_district.replace('محافظة ', '').replace('بلدية ', '').trim();
                          } else if (addr.city) {
                            city = addr.city;
                          }
                          
                          // استخراج الحي
                          let district = addr.suburb || addr.neighbourhood || addr.quarter || addr.city_district || '';
                          if (!district && addr.hamlet) {
                            district = addr.hamlet;
                          }
                          if (!district && addr.town && addr.town !== city) {
                            district = addr.town;
                          }
                          
                          // استخراج الشارع - مباشرة من addr.road
                          let street = '';
                          if (addr.road) {
                            street = addr.road;
                          } else if (addr.street) {
                            street = addr.street;
                          } else if (addr.highway) {
                            street = addr.highway;
                          } else if (addr.pedestrian) {
                            street = addr.pedestrian;
                          }
                          // إذا لم نجد الشارع، حاول استخراجه من display_name
                          if (!street && displayName) {
                            const parts = displayName.split(/[،,]/).map(p => p.trim());
                            for (const part of parts) {
                              if (part.includes('طريق') || part.includes('شارع')) {
                                street = part;
                                break;
                              }
                            }
                          }
                          console.log('Extracted street:', street);
                          
                          // استخراج المنطقة
                          const state = addr.state || '';
                          let region = '';
                          if (state.includes('الرياض')) region = 'riyadh';
                          else if (state.includes('مكة')) region = 'makkah';
                          else if (state.includes('المدينة')) region = 'madinah';
                          else if (state.includes('الشرقية')) region = 'eastern';
                          else if (state.includes('عسير')) region = 'asir';
                          else if (state.includes('تبوك')) region = 'tabuk';
                          else if (state.includes('حائل')) region = 'hail';
                          else if (state.includes('الحدود')) region = 'northern';
                          else if (state.includes('جازان')) region = 'jazan';
                          else if (state.includes('نجران')) region = 'najran';
                          else if (state.includes('الباحة')) region = 'bahah';
                          else if (state.includes('الجوف')) region = 'jawf';
                          else if (state.includes('القصيم')) region = 'qassim';
                          
                          // الرمز البريدي
                          const postalCode = addr.postcode || '';
                          
                          // طباعة البيانات المستخرجة للتصحيح
                          console.log('Extracted values:', { city, district, street, region, postalCode });
                          
                          // تحديث النموذج بالبيانات المستخرجة
                          setFormData(prev => {
                            const newData = {
                              ...prev,
                              latitude: lat.toFixed(6),
                              longitude: lng.toFixed(6),
                              city: city || prev.city || '',
                              district: district || prev.district || '',
                              street: street || prev.street || '',
                              region: region || prev.region || '',
                              postal_code: postalCode || prev.postal_code || ''
                            };
                            console.log('New formData:', newData);
                            return newData;
                          });
                          
                          setSearchResults([]);
                          setShowMapPicker(false);
                          success('تم اختيار الموقع واستخراج العنوان');
                        }}
                      >
                        <span className="result-icon">{I.map}</span>
                        <span className="result-text">{result.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="map-container leaflet-map">
                <MapContainer
                  center={[mapCenter.lat, mapCenter.lng]}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                  key={`${mapCenter.lat}-${mapCenter.lng}`}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[mapCenter.lat, mapCenter.lng]} />
                  <MapClickHandler 
                    onLocationSelect={(lat, lng) => {
                      setMapCenter({ lat, lng });
                      // Reverse geocoding
                      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ar`)
                        .then(res => res.json())
                        .then(data => {
                          if (data && data.address) {
                            const addr = data.address;
                            setFormData(prev => ({
                              ...prev,
                              latitude: lat.toFixed(6),
                              longitude: lng.toFixed(6),
                              city: addr.city || addr.town || addr.county?.replace('محافظة ', '') || prev.city || '',
                              district: addr.suburb || addr.neighbourhood || addr.quarter || prev.district || '',
                              street: addr.road || prev.street || '',
                              postal_code: addr.postcode || prev.postal_code || ''
                            }));
                            success('تم تحديد الموقع');
                          }
                        })
                        .catch(() => {
                          setFormData(prev => ({
                            ...prev,
                            latitude: lat.toFixed(6),
                            longitude: lng.toFixed(6)
                          }));
                        });
                    }}
                  />
                  <MapCenterUpdater center={[mapCenter.lat, mapCenter.lng]} />
                </MapContainer>
              </div>
              <div className="map-bottom-section">
                <div className="map-coords-input">
                  <div className="coord-field">
                    <label>خط العرض (Lat)</label>
                    <input
                      type="number"
                      step="any"
                      value={mapCenter.lat}
                      onChange={(e) => setMapCenter(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="coord-field">
                    <label>خط الطول (Lng)</label>
                    <input
                      type="number"
                      step="any"
                      value={mapCenter.lng}
                      onChange={(e) => setMapCenter(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
                <div className="map-quick-actions">
                  <button 
                    type="button" 
                    className="my-location-btn"
                    onClick={() => {
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          (position) => {
                            setMapCenter({
                              lat: position.coords.latitude,
                              lng: position.coords.longitude
                            });
                          },
                          () => showError('تعذر تحديد موقعك'),
                          { enableHighAccuracy: true, timeout: 5000 }
                        );
                      }
                    }}
                  >
                    📍 موقعي
                  </button>
                  <span className="divider">|</span>
                  <button type="button" onClick={() => setMapCenter({ lat: 24.7136, lng: 46.6753 })}>الرياض</button>
                  <button type="button" onClick={() => setMapCenter({ lat: 21.4225, lng: 39.8262 })}>جدة</button>
                  <button type="button" onClick={() => setMapCenter({ lat: 21.3891, lng: 39.8579 })}>مكة</button>
                  <button type="button" onClick={() => setMapCenter({ lat: 26.4207, lng: 50.0888 })}>الدمام</button>
                  <button type="button" onClick={() => setMapCenter({ lat: 24.4539, lng: 39.6142 })}>المدينة</button>
                </div>
              </div>
            </div>
            <div className="map-modal-footer">
              <button className="btn btn--secondary" onClick={() => setShowMapPicker(false)}>إلغاء</button>
              <button 
                className="btn btn--primary"
                onClick={() => {
                  const lat = mapCenter.lat;
                  const lng = mapCenter.lng;
                  setFormData(prev => ({
                    ...prev,
                    latitude: lat.toFixed(6),
                    longitude: lng.toFixed(6)
                  }));
                  setShowMapPicker(false);
                  success('تم تحديد الموقع بنجاح');
                  // استدعاء reverse geocoding للحصول على العنوان
                  reverseGeocode(lat, lng);
                }}
              >
                {I.check} تأكيد الموقع
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .add-ship-page {
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .page-header {
          margin-bottom: 32px;
        }
        
        .page-header__title h1 {
          font-size: 28px;
          font-weight: 700;
          color: var(--tenant-text);
          margin-bottom: 4px;
        }
        
        .page-header__title p {
          color: var(--tenant-text-muted);
          font-size: 15px;
        }
        
        /* Category Selection - Improved */
        .category-section {
          margin-bottom: 32px;
        }
        
        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--tenant-text);
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .category-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }
        
        @media (max-width: 900px) {
          .category-grid { grid-template-columns: repeat(2, 1fr); }
        }
        
        @media (max-width: 500px) {
          .category-grid { grid-template-columns: 1fr; }
        }
        
        .category-card {
          border: 2px solid var(--tenant-border);
          border-radius: 16px;
          padding: 20px 16px;
          cursor: pointer;
          transition: all 0.25s ease;
          background: var(--tenant-card-bg);
          text-align: center;
        }
        
        .category-card:hover {
          border-color: var(--tenant-primary);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
        }
        
        .category-card.selected {
          border-color: var(--tenant-primary);
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.02));
          box-shadow: 0 4px 20px rgba(59, 130, 246, 0.2);
        }
        
        .category-card input { display: none; }
        
        .category-card__content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        
        .category-card__content strong {
          font-size: 15px;
          font-weight: 600;
          color: var(--tenant-text);
        }
        
        .category-card__content small {
          color: var(--tenant-text-muted);
          font-size: 12px;
          line-height: 1.4;
        }
        
        .capacity-hint {
          margin-top: 8px;
          padding: 6px 12px;
          background: var(--tenant-bg);
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
          color: var(--tenant-primary);
        }
        
        /* Tabs - Modern Style */
        .tabs-wrapper {
          background: var(--tenant-card-bg);
          border-radius: 20px;
          padding: 24px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04);
        }
        
        .tabs {
          display: flex;
          gap: 6px;
          padding: 6px;
          background: var(--tenant-bg);
          border-radius: 12px;
          margin-bottom: 24px;
          overflow-x: auto;
        }
        
        .tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 18px;
          border: none;
          border-radius: 10px;
          background: transparent;
          color: var(--tenant-text-muted);
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s ease;
          position: relative;
          font-size: 14px;
          font-weight: 500;
        }
        
        .tab:hover {
          background: rgba(255,255,255,0.5);
          color: var(--tenant-text);
        }
        
        .tab.active {
          background: var(--tenant-primary);
          color: white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        
        .tab svg {
          width: 18px;
          height: 18px;
        }
        
        .error-dot {
          width: 8px;
          height: 8px;
          background: #ef4444;
          border-radius: 50%;
          position: absolute;
          top: 6px;
          left: 6px;
          border: 2px solid var(--tenant-card-bg);
        }
        
        .tab.active .error-dot {
          border-color: var(--tenant-primary);
        }
        
        /* Form Content */
        .form-content {
          padding: 8px 0;
        }
        
        .form-section-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--tenant-text);
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid var(--tenant-border);
        }
        
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }
        
        .form-group.full-width {
          grid-column: span 2;
        }
        
        @media (max-width: 768px) {
          .form-grid { grid-template-columns: 1fr; }
          .form-group.full-width { grid-column: span 1; }
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
        }
        
        .form-group label {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
          font-weight: 500;
          font-size: 14px;
          color: var(--tenant-text);
        }
        
        .required {
          color: #ef4444;
          font-size: 16px;
        }
        
        .verification-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          background: linear-gradient(135deg, var(--tenant-primary), #6366f1);
          color: white;
          border-radius: 50%;
          margin-right: 4px;
        }
        
        .verification-badge svg {
          width: 10px;
          height: 10px;
        }
        
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px 14px;
          border: 2px solid var(--tenant-border);
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.2s ease;
          background: var(--tenant-bg);
        }
        
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--tenant-primary);
          background: var(--tenant-card-bg);
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        
        .form-group .input-error {
          border-color: #ef4444;
          background: rgba(239, 68, 68, 0.05);
        }
        
        .error-text {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 6px;
          font-size: 12px;
          color: #ef4444;
        }
        
        /* URL with action button */
        .url-with-action {
          display: flex;
          gap: 10px;
        }
        
        .url-with-action input {
          flex: 1;
        }
        
        .url-with-action .btn {
          white-space: nowrap;
          border-radius: 10px;
        }
        
        /* File Upload - Modern */
        .file-upload {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .file-upload-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 28px 20px;
          border: 2px dashed var(--tenant-border);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.25s ease;
          background: var(--tenant-bg);
        }
        
        .file-upload-label:hover {
          border-color: var(--tenant-primary);
          background: rgba(59, 130, 246, 0.05);
        }
        
        .file-upload-label svg {
          width: 32px;
          height: 32px;
          color: var(--tenant-primary);
        }
        
        .file-upload-label span {
          font-size: 14px;
          color: var(--tenant-text-muted);
        }
        
        .file-hint {
          color: var(--tenant-text-muted);
          font-size: 12px;
          text-align: center;
        }
        
        /* Multi Select */
        .multi-select-group {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: 2px solid var(--tenant-border);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          background: var(--tenant-bg);
        }
        
        .checkbox-label:hover {
          border-color: var(--tenant-primary);
        }
        
        .checkbox-label input:checked + span {
          color: var(--tenant-primary);
          font-weight: 500;
        }
        
        /* Location Helper */
        .location-helper {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px dashed var(--tenant-border);
        }
        
        .helper-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.02));
          border-radius: 14px;
          border: 1px solid rgba(59, 130, 246, 0.15);
        }
        
        .helper-card svg {
          width: 36px;
          height: 36px;
          color: var(--tenant-primary);
          flex-shrink: 0;
        }
        
        .helper-card strong {
          color: var(--tenant-text);
          font-size: 14px;
        }
        
        .helper-card ol {
          margin: 10px 0;
          padding-right: 20px;
        }
        
        .helper-card li {
          margin-bottom: 6px;
          font-size: 13px;
          color: var(--tenant-text-muted);
        }
        
        .helper-card small {
          color: var(--tenant-text-muted);
          font-size: 12px;
        }
        
        /* Form Actions - Sticky Footer */
        .form-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 28px;
          padding: 20px 24px;
          background: var(--tenant-card-bg);
          border-radius: 16px;
          box-shadow: 0 -4px 20px rgba(0,0,0,0.05);
        }
        
        .nav-buttons {
          display: flex;
          gap: 12px;
        }
        
        .btn--success {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 12px 28px;
          font-weight: 600;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
        }
        
        .btn--success:hover {
          background: linear-gradient(135deg, #059669, #047857);
          transform: translateY(-1px);
        }
        
        .btn--secondary {
          background: var(--tenant-bg);
          border: 2px solid var(--tenant-border);
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 500;
        }
        
        .btn--primary {
          background: var(--tenant-primary);
          color: white;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 500;
        }
        
        .btn--sm {
          padding: 10px 14px;
          font-size: 13px;
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Progress indicator */
        .progress-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 20px;
        }
        
        .progress-step {
          flex: 1;
          height: 4px;
          background: var(--tenant-border);
          border-radius: 2px;
          transition: all 0.3s ease;
        }
        
        .progress-step.completed {
          background: var(--tenant-primary);
        }
        
        .progress-step.active {
          background: linear-gradient(90deg, var(--tenant-primary), #6366f1);
        }
        
        /* URL with action group */
        .url-with-action-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        
        .map-picker-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          border: 2px dashed var(--tenant-primary);
          border-radius: 10px;
          background: rgba(59, 130, 246, 0.05);
          color: var(--tenant-primary);
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .map-picker-btn:hover {
          background: rgba(59, 130, 246, 0.1);
          border-style: solid;
        }
        
        .map-picker-btn svg {
          width: 20px;
          height: 20px;
        }
        
        .btn--outline-primary {
          background: transparent;
          border: 2px solid var(--tenant-primary);
          color: var(--tenant-primary);
        }
        
        /* Map Modal */
        .map-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        
        .map-modal {
          background: var(--tenant-card-bg);
          border-radius: 16px;
          width: 100%;
          max-width: 550px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
        }
        
        .map-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--tenant-border);
          flex-shrink: 0;
        }
        
        .map-modal-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: var(--tenant-text);
        }
        
        .close-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: var(--tenant-bg);
          color: var(--tenant-text-muted);
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s;
        }
        
        .close-btn:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        
        .map-modal-body {
          padding: 16px 20px;
          overflow-y: auto;
          flex: 1;
        }
        
        .map-search-box {
          margin-bottom: 12px;
        }
        
        .search-input-wrapper {
          display: flex;
          gap: 8px;
        }
        
        .search-input-wrapper input {
          flex: 1;
          padding: 10px 14px;
          border: 2px solid var(--tenant-border);
          border-radius: 10px;
          font-size: 14px;
        }
        
        .search-input-wrapper input:focus {
          outline: none;
          border-color: var(--tenant-primary);
        }
        
        .search-btn {
          padding: 10px 16px;
          background: var(--tenant-primary);
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
        }
        
        .search-btn:hover {
          opacity: 0.9;
        }
        
        .search-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .search-results {
          margin-top: 8px;
          background: var(--tenant-bg);
          border-radius: 10px;
          border: 1px solid var(--tenant-border);
          max-height: 150px;
          overflow-y: auto;
        }
        
        .search-result-item {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          width: 100%;
          padding: 10px 12px;
          border: none;
          background: transparent;
          text-align: right;
          cursor: pointer;
          border-bottom: 1px solid var(--tenant-border);
          transition: background 0.2s;
        }
        
        .search-result-item:last-child {
          border-bottom: none;
        }
        
        .search-result-item:hover {
          background: rgba(59, 130, 246, 0.1);
        }
        
        .result-icon {
          flex-shrink: 0;
          color: var(--tenant-primary);
        }
        
        .result-icon svg {
          width: 16px;
          height: 16px;
        }
        
        .result-text {
          font-size: 13px;
          line-height: 1.4;
          color: var(--tenant-text);
        }
        
        .map-container {
          position: relative;
          width: 100%;
          height: 200px;
          border-radius: 10px;
          overflow: hidden;
          border: 2px solid var(--tenant-border);
        }
        
        .map-container.leaflet-map {
          height: 350px;
        }
        
        .map-container.leaflet-map .leaflet-container {
          height: 100%;
          width: 100%;
          border-radius: 10px;
          cursor: crosshair;
        }
        
        .map-bottom-section {
          display: flex;
          gap: 12px;
          margin-top: 12px;
          align-items: flex-end;
        }
        
        .map-coords-input {
          display: flex;
          gap: 8px;
          flex: 1;
        }
        
        .coord-field {
          flex: 1;
        }
        
        .coord-field label {
          display: block;
          font-size: 11px;
          color: var(--tenant-text-muted);
          margin-bottom: 4px;
        }
        
        .coord-field input {
          width: 100%;
          padding: 8px 10px;
          border: 2px solid var(--tenant-border);
          border-radius: 8px;
          font-size: 13px;
          text-align: center;
          direction: ltr;
        }
        
        .coord-field input:focus {
          outline: none;
          border-color: var(--tenant-primary);
        }
        
        .map-quick-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          margin-top: 12px;
        }
        
        .map-quick-actions .divider {
          color: var(--tenant-border);
          font-size: 14px;
        }
        
        .map-quick-actions button {
          padding: 6px 12px;
          border: 1px solid var(--tenant-border);
          border-radius: 20px;
          background: var(--tenant-bg);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .map-quick-actions button:hover {
          border-color: var(--tenant-primary);
          color: var(--tenant-primary);
          background: rgba(59, 130, 246, 0.05);
        }
        
        .my-location-btn {
          background: var(--tenant-primary) !important;
          color: white !important;
          border-color: var(--tenant-primary) !important;
          font-weight: 500;
        }
        
        .my-location-btn:hover {
          background: #2563eb !important;
          border-color: #2563eb !important;
          color: white !important;
        }
        
        .map-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid var(--tenant-border);
          background: var(--tenant-bg);
          flex-shrink: 0;
        }
        
        .map-modal-footer .btn {
          padding: 10px 20px;
          font-size: 14px;
        }
        
        /* Responsive Styles */
        @media (max-width: 768px) {
          .map-modal {
            max-width: 100%;
            max-height: 95vh;
            margin: 8px;
            border-radius: 12px;
          }
          
          .map-modal-header {
            padding: 12px 16px;
          }
          
          .map-modal-header h3 {
            font-size: 14px;
          }
          
          .map-modal-body {
            padding: 12px 16px;
          }
          
          .search-input-wrapper {
            flex-direction: column;
          }
          
          .search-btn {
            width: 100%;
          }
          
          .map-container {
            height: 180px;
          }
          
          .map-bottom-section {
            flex-direction: column;
            gap: 8px;
          }
          
          .map-coords-input {
            width: 100%;
          }
          
          .map-quick-actions {
            justify-content: center;
          }
          
          .map-modal-footer {
            padding: 12px 16px;
            flex-wrap: wrap;
          }
          
          .map-modal-footer .btn {
            flex: 1;
            min-width: 120px;
          }
        }
        
        @media (min-width: 769px) and (max-width: 1024px) {
          .map-modal {
            max-width: 600px;
          }
          
          .map-container {
            height: 250px;
          }
        }
        
        @media (min-width: 1025px) {
          .map-modal {
            max-width: 700px;
          }
          
          .map-container {
            height: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default AddShipPage;
